import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Autocomplete } from 'components';
import EmployeesServices from 'services/EmployeesServices';

/**
 * OfficerDropDown - Dropdown for selecting officers from a specific organization.
 *
 * @param {object} props
 * @param {string} props.name - Name of the field (for form handling).
 * @param {string} [props.label] - Label for the field.
 * @param {any} props.value - Value (controlled, can be string, object, or array for multiple).
 * @param {function} props.onChange - Change handler (event, value) => void.
 * @param {boolean} [props.required] - Required field.
 * @param {Array} [props.validators] - Validation rules.
 * @param {Array} [props.errorMessages] - Validation error messages.
 * @param {string} props.organizationId - Organization ID to fetch officers from.
 * @param {boolean} [props.disabled] - Whether the dropdown is disabled.
 * @returns {JSX.Element}
 */

const OfficerDropDown = ({
	name,
	label,
	value,
	onChange,
	required = false,
	validators = [],
	errorMessages = [],
	organizationId,
	disabled = false,
	type,
  excludeIds = [],
}) => {
	const [officers, setOfficers] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const excludeIdsSafe = useMemo(() => (excludeIds || []).filter(Boolean), [excludeIds]);
	const excludeIdsKey = useMemo(() => excludeIdsSafe.join('|'), [excludeIdsSafe]);

	const fetchOfficers = useCallback(async () => {
		if (!organizationId) {
			setOfficers([]);
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const params = {
				organization_id: organizationId,
				status: 'Active',
			};

			// Apply optional type filter if provided
			if (type) {
				params.type = type;
			}

			const response = await EmployeesServices.get(params);
			const officerData = response?.data?.view?.data || response?.data?.data || [];

			if (Array.isArray(officerData)) {
				const formattedOfficers = officerData.map(officer => ({
					id: officer.id,
					label: officer.name || 'Unknown Officer',
					value: officer.id,
					officerType: officer.type || officer.office_type || '',
					designation: officer.designationDetails?.name || officer.designation || '',
					email: officer.email || '',
					phone: officer.contact_no || ''
				}));

				// Apply optional client-side exclusions
				const excludeSet = new Set(excludeIdsSafe);
				const filtered = formattedOfficers.filter(o => !excludeSet.has(o.id) && !excludeSet.has(o.value));
				setOfficers(filtered);
			} else {
				setOfficers([]);
			}
		} catch (err) {
			console.error('Failed to fetch officers:', err);
			setError('Failed to load officers');
			setOfficers([]);
		} finally {
			setLoading(false);
		}
	}, [organizationId, type, excludeIdsKey]);

	useEffect(() => {
		fetchOfficers();
	}, [fetchOfficers]);

	// Find the selected officer object based on the value
	const selectedOfficer = officers.find(officer => officer.value === value) || null;

	return (
		<div>
			<Autocomplete
				name={name}
				label={label}
				value={selectedOfficer}
				onChange={(event, newValue) => {
					if (onChange) {
						onChange(event, newValue?.value || null);
					}
				}}
				options={officers}
				getOptionLabel={(option) => option.label || ''}
				isOptionEqualToValue={(option, value) => option.value === value.value}
				loading={loading}
				disabled={disabled || !organizationId}
				required={required}
				validators={validators}
				errorMessages={errorMessages}
				noOptionsText={
					!organizationId 
						? "No organization selected" 
						: error 
							? error 
							: loading 
								? "Loading..." 
								: "No officers found"
				}
			/>
		</div>
	);
};

export default OfficerDropDown;